import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/hooks/use-auth";
import { getMyCredits } from "@/lib/account.functions";
import { getDealDetail, listMyDeals, type DealSummary } from "@/lib/deals.functions";
import { getMyDealsModelReviewStatus } from "@/lib/model-review.functions";
import { getTeamReportQuotes } from "@/lib/report-quote.functions";
import {
  listReportTakeaways,
  type TakeawayKind,
} from "@/lib/report-takeaways.functions";
import {
  listDealAgentDartFlags,
  setDealAgentDartEnabled,
  listDealGoals,
  listDealAgentActionSummaries,
  listDealNextStepInputs,
  listDealLatestActivity,
  listDealEngagedContacts,
  listDealSendModes,
} from "@/lib/agent-actions.functions";
import { listDealPlanSummaries } from "@/lib/deal-plans.functions";
import { DealPageHeader } from "@/components/deal-page-header";
import { DealNav } from "@/components/deal-nav";
import { DealCard } from "@/components/dashboard/deal-card";
import { DealAgentDartSummary } from "@/components/deal-agentdart-summary";
import { DealOutreachRedirectCard } from "@/components/deal-outreach-redirect-card";
import { DealConferencesCard } from "@/components/deal-conferences-card";

export const Route = createFileRoute("/_authed/deals/$dealId")({
  head: () => ({
    meta: [
      { title: "Deal overview: CapitalDart" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: DealOverviewPage,
});

function DealOverviewPage() {
  const { dealId } = Route.useParams();
  const { user } = useAuth();
  const qc = useQueryClient();

  const fetchDetail = useServerFn(getDealDetail);
  const detail = useQuery({
    queryKey: ["deal-detail", dealId],
    queryFn: () => fetchDetail({ data: { dealId } }),
  });

  // Every read below reuses the dashboard's team-batched functions under the
  // dashboard's query keys, so invalidations flow both ways for free.
  const fetchCredits = useServerFn(getMyCredits);
  const credits = useQuery({
    queryKey: ["my-credits", user?.id],
    queryFn: () => fetchCredits(),
    enabled: !!user,
  });

  const fetchDeals = useServerFn(listMyDeals);
  const deals = useQuery({
    queryKey: ["my-recent-deals", user?.id],
    queryFn: () => fetchDeals({ data: {} }),
    enabled: !!user,
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
  });
  const deal = React.useMemo(
    () => (deals.data ?? []).find((d) => d.id === dealId),
    [deals.data, dealId],
  );

  const dealIds = React.useMemo(
    () => (deals.data ?? []).map((d) => d.id),
    [deals.data],
  );
  const fetchReviewStatuses = useServerFn(getMyDealsModelReviewStatus);
  const reviewStatuses = useQuery({
    queryKey: ["my-deals-model-review-status", user?.id, dealIds.join(",")],
    queryFn: () => fetchReviewStatuses({ data: { dealIds } }),
    enabled: !!user && dealIds.length > 0,
    staleTime: 30_000,
  });

  const fetchQuotes = useServerFn(getTeamReportQuotes);
  const reportQuotes = useQuery({
    queryKey: ["my-team-report-quotes", user?.id],
    queryFn: () => fetchQuotes({}),
    enabled: !!user,
    staleTime: 60_000,
  });

  const fetchDealAgentDartFlags = useServerFn(listDealAgentDartFlags);
  const dealAgentDartFlags = useQuery({
    queryKey: ["agentdart-deal-flags", user?.id],
    queryFn: () => fetchDealAgentDartFlags({}),
    enabled: !!user,
  });
  const agentDartEnabled = React.useMemo(
    () =>
      (dealAgentDartFlags.data?.deals ?? []).find((d) => d.id === dealId)
        ?.enabled ?? false,
    [dealAgentDartFlags.data, dealId],
  );

  const fetchDealGoals = useServerFn(listDealGoals);
  const dealGoals = useQuery({
    queryKey: ["deal-goals", user?.id],
    queryFn: () => fetchDealGoals({}),
    enabled: !!user,
  });
  const goal =
    (dealGoals.data?.goals ?? []).find((g) => g.deal_id === dealId) ?? null;

  const fetchSendModes = useServerFn(listDealSendModes);
  const sendModes = useQuery({
    queryKey: ["deal-send-modes", user?.id],
    queryFn: () => fetchSendModes({}),
    enabled: !!user,
  });
  const sendMode = ((sendModes.data?.deals ?? []).find((d) => d.id === dealId)
    ?.mode ?? "drafts") as "drafts" | "direct";

  const fetchActionSummaries = useServerFn(listDealAgentActionSummaries);
  const actionSummaries = useQuery({
    queryKey: ["agent-actions-summary", user?.id],
    queryFn: () => fetchActionSummaries({}),
    enabled: !!user,
    staleTime: 30_000,
  });

  const fetchPlanSummaries = useServerFn(listDealPlanSummaries);
  const planSummaries = useQuery({
    queryKey: ["deal-plan-summaries"],
    queryFn: () => fetchPlanSummaries({}),
    enabled: !!user,
    staleTime: 30_000,
  });

  const fetchNextStepInputs = useServerFn(listDealNextStepInputs);
  const nextStepInputs = useQuery({
    queryKey: ["deal-next-step-inputs", user?.id],
    queryFn: () => fetchNextStepInputs({}),
    enabled: !!user,
    staleTime: 30_000,
  });

  const fetchEngagedContacts = useServerFn(listDealEngagedContacts);
  const engagedContacts = useQuery({
    queryKey: ["deal-engaged-contacts"],
    queryFn: () => fetchEngagedContacts({}),
    enabled: !!user,
    staleTime: 30_000,
  });

  const fetchLatestActivity = useServerFn(listDealLatestActivity);
  const latestActivity = useQuery({
    queryKey: ["deal-latest-activity", user?.id],
    queryFn: () => fetchLatestActivity({}),
    enabled: !!user,
    staleTime: 30_000,
  });

  // Takeaways use the same key shape as the dashboard, keyed on the joined
  // report ids, here just this deal's latest reports.
  const fetchReportTakeaways = useServerFn(listReportTakeaways);
  const takeawayReportIds = React.useMemo(() => {
    const out: { kind: TakeawayKind; report_id: string }[] = [];
    const KIND_FIELDS: [TakeawayKind, keyof DealSummary][] = [
      ["deck_studio", "latest_deck_studio_report_id"],
      ["model_review", "latest_model_review_report_id"],
      ["raise_strategy", "latest_raise_strategy_report_id"],
      ["market_comps", "latest_market_comps_report_id"],
      ["term_sheet", "latest_term_sheet_report_id"],
      ["deal_update", "latest_deal_update_report_id"],
    ];
    if (!deal) return out;
    for (const [kind, field] of KIND_FIELDS) {
      const id = deal[field];
      if (typeof id === "string" && id) out.push({ kind, report_id: id });
    }
    return out;
  }, [deal]);
  const reportTakeaways = useQuery({
    queryKey: [
      "report-takeaways",
      user?.id,
      takeawayReportIds.map((r) => r.report_id).join(","),
    ],
    queryFn: () => fetchReportTakeaways({ data: { reports: takeawayReportIds } }),
    enabled: !!user && takeawayReportIds.length > 0,
    staleTime: 60_000,
    refetchInterval: (query) => {
      const byId = query.state.data?.byReportId;
      if (!byId) return false;
      return Object.values(byId).some((t) => t.status === "pending")
        ? 15_000
        : false;
    },
  });

  const setDealAgentDart = useServerFn(setDealAgentDartEnabled);
  const handleAgentDartToggle = async (id: string, enabled: boolean) => {
    const flagsKey = ["agentdart-deal-flags", user?.id];
    const prev = qc.getQueryData(flagsKey);
    qc.setQueryData(
      flagsKey,
      (old: { deals: { id: string; name: string; enabled: boolean }[] } | undefined) =>
        old
          ? { deals: old.deals.map((d) => (d.id === id ? { ...d, enabled } : d)) }
          : old,
    );
    try {
      await setDealAgentDart({ data: { dealId: id, enabled } });
      toast.success(
        enabled
          ? "AgentDart is watching this deal. You will get a short daily email when it has follow-ups ready."
          : "AgentDart is off for this deal",
      );
      void qc.invalidateQueries({ queryKey: ["agent-actions"] });
      void qc.invalidateQueries({ queryKey: ["agent-actions-count"] });
    } catch (e) {
      qc.setQueryData(flagsKey, prev);
      toast.error(e instanceof Error ? e.message : "Failed to update AgentDart");
    }
  };

  const remaining = credits.data?.credits_remaining ?? 0;
  const canRun = remaining > 0 || (credits.data?.trial_available ?? false);

  return (
    <main className="mx-auto max-w-[640px] px-4 pb-16 pt-6 lg:max-w-[1320px]">
      <div className="space-y-6">
        <DealPageHeader dealId={dealId} />
        <DealNav
          dealId={dealId}
          active="overview"
          isDemo={
            (detail.data?.deal as { is_demo?: boolean } | undefined)?.is_demo ===
            true
          }
        />
        {deals.isLoading ? (
          <div className="h-40 animate-pulse rounded-xl border border-brand-line bg-brand-paper-2" />
        ) : deal ? (
          <DealCard
            deal={deal}
            hideNameAndOpen
            hideAgentDartSection
            canRun={canRun}
            creditsRemaining={remaining}
            freeIrrRemaining={
              credits.data?.free_deck_studio_reports_remaining ?? 0
            }
            modelReview={reviewStatuses.data?.statuses?.[deal.id]}
            quotes={reportQuotes.data}
            agentDartEnabled={deal.is_demo ? undefined : agentDartEnabled}
            onAgentDartToggle={handleAgentDartToggle}
            goal={deal.is_demo ? undefined : goal}
            actionSummary={actionSummaries.data?.byDealId?.[deal.id] ?? null}
            nextStepInputs={nextStepInputs.data?.byDealId?.[deal.id] ?? null}
            activity={latestActivity.data?.byDealId?.[deal.id] ?? null}
            engaged={engagedContacts.data?.byDealId?.[deal.id] ?? null}
            takeawaysByReportId={reportTakeaways.data?.byReportId}
            planSummary={planSummaries.data?.byDealId?.[deal.id]}
            overviewLeftContent={
              deal.is_demo ? null : (
                <>
                  <DealAgentDartSummary
                    dealId={dealId}
                    agentDartEnabled={agentDartEnabled}
                    goal={goal}
                    actionSummary={actionSummaries.data?.byDealId?.[deal.id] ?? null}
                    sendMode={sendMode}
                    loading={dealGoals.isLoading || dealAgentDartFlags.isLoading}
                    embedded
                  />
                  <DealConferencesCard dealId={dealId} embedded />
                </>
              )
            }
          />
        ) : null}
        <DealOutreachRedirectCard dealId={dealId} />
      </div>
    </main>
  );
}
